import { Calendar, Clock, Video, MapPin } from "lucide-react";

function parseDate(dateStr) {
  if (!dateStr) return null;
  // Try YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return new Date(dateStr + "T00:00:00");
  }
  // Try M/D/YYYY format (from IntakeQ)
  const mdyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (mdyMatch) {
    return new Date(parseInt(mdyMatch[3]), parseInt(mdyMatch[1]) - 1, parseInt(mdyMatch[2]));
  }
  // Fallback
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return dateStr || "";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function formatMonth(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return "";
  return d.toLocaleDateString("en-US", { month: "short" });
}

function formatDay(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return "";
  return d.getDate();
}

const statusColors = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  Confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  Scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  rescheduled: "bg-amber-50 text-amber-700 border-amber-200",
  no_show: "bg-gray-50 text-gray-700 border-gray-200",
};

export default function AppointmentCard({ appointment, compact = false }) {
  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 bg-card rounded-xl border border-border" data-testid="appointment-card-compact">
        <div className="w-12 h-12 rounded-xl bg-accent flex flex-col items-center justify-center shrink-0">
          <span className="text-[10px] font-medium text-muted-foreground uppercase">{formatMonth(appointment.date)}</span>
          <span className="text-lg font-bold text-foreground leading-none">{formatDay(appointment.date)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{appointment.appointment_type}</p>
          <p className="text-xs text-muted-foreground">{appointment.time} - {appointment.provider_name}</p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusColors[appointment.status] || statusColors.scheduled}`}>
          {appointment.status}
        </span>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-4 animate-fade-in" data-testid="appointment-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-foreground">{appointment.appointment_type}</h3>
          <p className="text-sm text-muted-foreground">{appointment.provider_name}</p>
          {appointment.provider_title && <p className="text-xs text-muted-foreground">{appointment.provider_title}</p>}
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusColors[appointment.status] || statusColors.scheduled}`}>
          {appointment.status}
        </span>
      </div>
      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
        <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /><span>{formatDate(appointment.date)}</span></div>
        <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /><span>{appointment.time}</span></div>
        <div className="flex items-center gap-1.5">
          {appointment.location === "Telehealth" ? <Video className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}
          <span>{appointment.location || "Telehealth"}</span>
        </div>
      </div>
    </div>
  );
}
